import { interceptorMiddleware } from '../../../shared/services/interceptor.service';
import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethods } from '../../../shared/constants/application.constant';
import { apiUrlBusinessInfo } from '../../../modules/businessInfo/utils/apiUrl.businessInfo';
import {
    CategoryLevelOneRequest,
    CategoryLevelOneResponse,
    CategoryLevelThreeFormRequest,
    CategoryLevelThreeResponse,
    CategoryLevelTwoRequest,
    CategorySubLevelThreeFormRequest,
    EntertainmentTypeResponse,
} from '../constants/types.categories';
import { apiUrlCategories } from '../constants/apiUrl.categories';
import { mapFormDataFields } from '../../../shared/utils/helper';

// Define a service using a base URL and expected endpoints
export const categoriesApi = createApi({
    reducerPath: 'categories',
    baseQuery: interceptorMiddleware,
    tagTypes: ['categories'],
    endpoints: (builder) => ({
        getEntertainmentTypes: builder.query<EntertainmentTypeResponse, {}>({
            query: () => ({
                url: `${apiUrlBusinessInfo.GET_META}`,
                method: 'GET',
            }),
            providesTags: ['categories'],
        }),
        createCategoryLevelOne: builder.mutation({
            query: (data: CategoryLevelOneRequest) => {
                const requestData = {
                    name: data.name,
                    entertainment_type: Number(data.entertainment_type),
                };
                return {
                    url: apiUrlCategories.CATEGORY_LEVEL_ONE,
                    method: HttpMethods.POST,
                    body: requestData,
                };
            },
        }),
        getCategoryLevelOne: builder.query<
            CategoryLevelOneResponse,
            { entertainment_type: number }
        >({
            query: ({ entertainment_type }) => ({
                url: `${apiUrlCategories.CATEGORY_LEVEL_ONE}?entertainment_type=${entertainment_type}`,
                method: 'GET',
            }),
            providesTags: ['categories'],
        }),
        getCategoryLevelTwo: builder.query<
            CategoryLevelOneResponse,
            { id: number }
        >({
            query: ({ id }) => ({
                url: `${apiUrlCategories.CATEGORY_LEVEL_TWO}${id}/`,
                method: 'GET',
            }),
            providesTags: ['categories'],
        }),
        getAllCategoryLevelTwo: builder.query<CategoryLevelOneResponse, {}>({
            query: () => ({
                url: apiUrlCategories.CATEGORY_LEVEL_TWO,
                method: 'GET',
            }),
            providesTags: ['categories'],
        }),
        createCategoryLevelTwo: builder.mutation({
            query: (data: CategoryLevelTwoRequest) => {
                return {
                    url: apiUrlCategories.CATEGORY_LEVEL_TWO,
                    method: HttpMethods.POST,
                    body: data,
                };
            },
        }),
        getCategoryLevelThree: builder.query<
            CategoryLevelThreeResponse,
            number
        >({
            query: (id: number) => ({
                url: `${apiUrlCategories.CATEGORY_LEVEL_THREE}${id}/`,
                method: 'GET',
            }),
            providesTags: ['categories'],
        }),
        createCategoryLevelThree: builder.mutation({
            query: (data: CategoryLevelThreeFormRequest) => {
                return {
                    url: apiUrlCategories.CATEGORY_LEVEL_THREE_HEADING,
                    method: HttpMethods.POST,
                    body: data,
                };
            },
        }),
        createCategorySubLevelThree: builder.mutation({
            query: (data: CategorySubLevelThreeFormRequest) => {
                const requestData = {
                    ...data,
                    icon: data?.s3MediaUrl,
                };
                delete requestData['s3MediaUrl'];
                return {
                    url: apiUrlCategories.CATEGORY_LEVEL_THREE,
                    method: HttpMethods.POST,
                    body: requestData,
                };
            },
        }),
        deleteLevel1: builder.mutation<
            { message: string },
            { id: number; type: boolean; levelOneId?: number }
        >({
            query: ({ id, type, levelOneId }) => {
                const levelOne = levelOneId ? `?level_1=${levelOneId}` : '';
                return {
                    url: `${type
                        ? apiUrlCategories.CATEGORY_LEVEL_ONE
                        : apiUrlCategories.CATEGORY_LEVEL_TWO
                        }${id}/${levelOne}`,
                    method: HttpMethods.DELETE,
                };
            },
        }),
        deleteLevel3: builder.mutation<
            { message: string },
            { id: number; isHeading: boolean }
        >({
            query: ({ id, isHeading }) => {
                return {
                    url: `${isHeading
                        ? apiUrlCategories.CATEGORY_LEVEL_THREE_HEADING
                        : apiUrlCategories.CATEGORY_LEVEL_THREE
                        }${id}/`,
                    method: HttpMethods.DELETE,
                };
            },
        }),
        updateCategorySubLevelThree: builder.mutation<
            {
                message: string;
            },
            {
                data: CategorySubLevelThreeFormRequest;
                initialValues: string;
            }
        >({
            query: ({ data, initialValues }) => {
                const requestData: any = {};
                if (data.icon as any !== initialValues) {
                    requestData.icon = data.s3MediaUrl;
                }
                return {
                    url: `${apiUrlCategories.CATEGORY_LEVEL_THREE}${data?.id}/`,
                    method: HttpMethods.PATCH,
                    body: requestData,
                };
            },
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetEntertainmentTypesQuery,
    useCreateCategoryLevelOneMutation,
    useGetCategoryLevelOneQuery,
    useLazyGetCategoryLevelTwoQuery,
    useGetAllCategoryLevelTwoQuery,
    useCreateCategoryLevelTwoMutation,
    useGetCategoryLevelThreeQuery,
    useCreateCategoryLevelThreeMutation,
    useCreateCategorySubLevelThreeMutation,
    useDeleteLevel1Mutation,
    useDeleteLevel3Mutation,
    useUpdateCategorySubLevelThreeMutation,
} = categoriesApi;
